import React from "react";
import LinkifyIt from "linkify-it";
import tlds from "tlds";

// Initialize linkify with TLD (top-level domain) support and enable www pattern
const linkify = new LinkifyIt();
linkify
  .tlds(tlds) // Add list of known TLDs (top level domains)
  .set({ fuzzyIP: true }) // Enable fuzzy IP matching
  .set({ fuzzyLink: true }) // Enable fuzzy link matching
  .add("www:", "http://"); // Enable 'www' URLs

interface LinkifyTextProps {
  text: string;
  className?: string;
}

interface LinkifyMatch {
  index: number;
  lastIndex: number;
  raw: string;
  text: string;
  url: string;
}

const LinkifyText: React.FC<LinkifyTextProps> = ({ text, className }) => {
  if (!text) {
    return null;
  }

  // Find all links in the text
  const matches = linkify.match(text) || [];

  if (matches.length === 0) {
    // If no links, just return the text
    return <p className={className}>{text}</p>;
  }

  // Create an array to hold all text segments and links
  const elements: JSX.Element[] = [];
  let lastIndex = 0;

  matches.forEach((match: LinkifyMatch, index: number) => {
    // Add text before the link
    if (match.index > lastIndex) {
      elements.push(
        <span key={`text-${index}`} style={{ whiteSpace: "pre-wrap" }}>
          {text.slice(lastIndex, match.index)}
        </span>
      );
    }

    // Add the link
    elements.push(
      <a
        key={`link-${index}`}
        href={match.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`hover:underline !text-blue-700 hover:!text-blue-800 visited:!text-purple-600 dark:!text-blue-400 dark:hover:!text-blue-500 dark:visited:!text-purple-400 transition-colors`}
      >
        {match.text}
      </a>
    );

    lastIndex = match.lastIndex;
  });

  // Add remaining text after the last link
  if (lastIndex < text.length) {
    elements.push(
      <span key="text-end" style={{ whiteSpace: "pre-wrap" }}>
        {text.slice(lastIndex)}
      </span>
    );
  }

  return <p className={className}>{elements}</p>;
};

export default LinkifyText;
